import type { JcCookieConsent } from '../../types/cookie-consent';
import { JC_CONSENT_COOKIE_NAME, JC_CONSENT_SCHEMA_VERSION } from './constants';

/**
 * Extract a single cookie value from a `Cookie` header or `document.cookie` string.
 */
export function getRawCookieValue(cookieHeaderOrDocumentCookie: string, name: string): string | null {
  if (!cookieHeaderOrDocumentCookie || !name) return null;
  const parts = cookieHeaderOrDocumentCookie.split(';').map((p) => p.trim());
  for (const part of parts) {
    if (!part) continue;
    const eq = part.indexOf('=');
    if (eq <= 0) continue;
    const key = part.slice(0, eq).trim();
    if (key !== name) continue;
    const value = part.slice(eq + 1);
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parse consent JSON from a stored cookie value (decoded string), or null if missing/invalid.
 */
export function parseConsentJson(raw: string | null | undefined): JcCookieConsent | null {
  if (raw == null || raw === '') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  if (parsed.v !== JC_CONSENT_SCHEMA_VERSION) return null;
  if (typeof parsed.analytics !== 'boolean') return null;
  if (typeof parsed.updatedAt !== 'string' || parsed.updatedAt.length === 0) return null;
  return {
    v: JC_CONSENT_SCHEMA_VERSION,
    analytics: parsed.analytics,
    updatedAt: parsed.updatedAt,
  };
}

/**
 * Parse consent from a full Cookie header or document.cookie string.
 */
export function parseConsentFromCookieString(
  cookieHeaderOrDocumentCookie: string | null | undefined,
): JcCookieConsent | null {
  if (cookieHeaderOrDocumentCookie == null || cookieHeaderOrDocumentCookie === '') return null;
  const raw = getRawCookieValue(cookieHeaderOrDocumentCookie, JC_CONSENT_COOKIE_NAME);
  return parseConsentJson(raw);
}
